import React from 'react';
import { MessagesSquare, Users, BookOpen, Film, Check } from 'lucide-react';
import Newsletter from '../components/Newsletter';
import PitchForm from '../components/PitchForm';
import { useToast } from '../hooks/use-toast';

const kinds = [
  { icon: MessagesSquare, label: 'A story' },
  { icon: Users, label: 'A problem' },
  { icon: BookOpen, label: 'A forum idea' },
  { icon: Film, label: 'A documentary subject' },
];

const checklist = [
  'No professional experience required - only a story worth sharing.',
  'We co-create programmes with you, from the first conversation.',
  'Every pitch gets a personal reply, usually within a few working days.',
];

const Pitch = () => {
  const { toast } = useToast();

  const handleSubmit = ({ ok, message }) => {
    if (ok) {
      toast({ title: 'Pitch your story', description: 'Thanks - we read every application and reply personally within a few working days.' });
    } else {
      toast({ title: 'Could not submit', description: message });
    }
  };

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-[#0c1119] pt-36 pb-16 border-b border-white/10">
        <img src="/events/pitch-hero.png" alt="" aria-hidden="true" className="absolute inset-0 h-full w-full object-cover opacity-70" />
        <div className="absolute inset-0 bg-gradient-to-r from-[#0c1119] via-[#0c1119]/80 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0c1119] via-transparent to-transparent" />
        <div className="relative max-w-[1200px] mx-auto px-6 lg:px-10 max-w-3xl">
          <span className="dctv-eyebrow text-[#e8743b]">Pitch us a programme</span>
          <h1 className="mt-4 font-serif-dctv text-5xl sm:text-6xl font-bold text-white">Have a story we should tell?</h1>
          <p className="mt-5 text-lg leading-relaxed text-[#e7e9ee]/75">
            Community workers, scholars, students, parents, artists, lawyers, entrepreneurs - if you have a story, a problem, a forum idea or a documentary subject, we want to hear about it. DCTV programmes are co-created with the community from the very first conversation.
          </p>
        </div>
      </section>

      {/* Kinds */}
      <section className="bg-[#0a0e16] py-16 border-b border-white/10">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {kinds.map(({ icon: Icon, label }) => (
              <div key={label} className="rounded-2xl border border-white/10 bg-[#111726] p-6">
                <Icon className="w-6 h-6 text-[#e8743b]" />
                <p className="mt-3 font-serif-dctv text-lg font-bold text-white">{label}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Application */}
      <section className="bg-[#0a0e16] py-20">
        <div className="max-w-[1200px] mx-auto px-6 lg:px-10 grid gap-12 lg:grid-cols-[1fr_1.1fr] items-start">
          <div>
            <span className="dctv-eyebrow text-[#e8743b]">Application</span>
            <h2 className="mt-3 font-serif-dctv text-3xl sm:text-4xl font-bold text-white">Tell us about you.</h2>
            <p className="mt-4 text-[#e7e9ee]/70 leading-relaxed">
              No CV needed. Drop us a few lines - we read every single application and reply personally within a few working days.
            </p>
            <ul className="mt-8 space-y-4">
              {checklist.map((item) => (
                <li key={item} className="flex items-start gap-3">
                  <span className="mt-0.5 grid w-6 h-6 shrink-0 place-items-center rounded-full bg-[#e8743b]/15 text-[#e8743b]">
                    <Check className="w-4 h-4" />
                  </span>
                  <span className="text-sm leading-relaxed text-[#e7e9ee]/75">{item}</span>
                </li>
              ))}
            </ul>
          </div>
          <PitchForm onSubmit={handleSubmit} />
        </div>
      </section>

      <Newsletter />
    </div>
  );
};

export default Pitch;
